import React, { useEffect, useMemo, useState } from 'react';
import { AppShell } from '@/components/nav/AppShell';
import { customersApi } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Plus, Search, Mail, Phone, MapPin, Tag } from 'lucide-react';
import { CUSTOMERS } from '@/constants/testIds';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { EmptyState } from '@/components/empty/EmptyState';
import { ListSkeleton } from '@/components/common/ListSkeleton';
import { Badge } from '@/components/ui/badge';

export default function CustomersPage() {
  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [q, setQ] = useState('');
  const [openNew, setOpenNew] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', phone: '', service_address: '', billing_address: '', tags: '', notes: '' });

  const load = async () => {
    try {
      const data = await customersApi.list();
      setItems(data);
    } catch {
      // surfaced by the api response interceptor
    } finally {
      setLoaded(true);
    }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const s = q.toLowerCase().trim();
    if (!s) return items;
    return items.filter(c => (c.name || '').toLowerCase().includes(s) || (c.email || '').toLowerCase().includes(s) || (c.phone || '').includes(s));
  }, [items, q]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, tags: form.tags.split(',').map(s => s.trim()).filter(Boolean) };
      await customersApi.create(payload);
      toast.success('Customer created');
      setOpenNew(false);
      setForm({ name: '', email: '', phone: '', service_address: '', billing_address: '', tags: '', notes: '' });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to create');
    }
  };

  return (
    <AppShell title="Customers">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between [&>*:last-child]:w-full sm:[&>*:last-child]:w-auto mb-6">
        <div>
          <h1 className="font-display text-2xl md:text-3xl font-semibold tracking-tight">Customers</h1>
          <p className="text-sm text-muted-foreground">All your customers and their properties.</p>
        </div>
        <Dialog open={openNew} onOpenChange={setOpenNew}>
          <DialogTrigger asChild>
            <Button data-testid={CUSTOMERS.createButton}><Plus className="h-4 w-4 mr-2" />New customer</Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle>New customer</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="space-y-3">
              <div><Label>Name *</Label><Input required value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} data-testid={CUSTOMERS.formName} /></div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div><Label>Email</Label><Input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} data-testid={CUSTOMERS.formEmail} /></div>
                <div><Label>Phone</Label><Input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} data-testid={CUSTOMERS.formPhone} /></div>
              </div>
              <div><Label>Service address</Label><Input value={form.service_address} onChange={e => setForm({ ...form, service_address: e.target.value })} data-testid={CUSTOMERS.formAddress} /></div>
              <div><Label>Tags (comma separated)</Label><Input placeholder="Vacational Rental, VIP" value={form.tags} onChange={e => setForm({ ...form, tags: e.target.value })} data-testid={CUSTOMERS.formTags} /></div>
              <div><Label>Notes</Label><Textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} data-testid={CUSTOMERS.formNotes} /></div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setOpenNew(false)}>Cancel</Button>
                <Button type="submit" data-testid={CUSTOMERS.formSubmit}>Create</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="mb-4 flex items-center gap-2">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Search customers…" value={q} onChange={e => setQ(e.target.value)} className="pl-9" />
        </div>
      </div>

      {!loaded ? (
        <ListSkeleton />
      ) : filtered.length === 0 ? (
        <EmptyState
          title="No customers yet"
          description="Add your first customer to start creating jobs and estimates."
          cta={<Button onClick={() => setOpenNew(true)}><Plus className="h-4 w-4 mr-2" />New customer</Button>}
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {filtered.map(c => (
            <Link to={`/customers/${c.id}`} key={c.id} data-testid={CUSTOMERS.listRow} className="block">
              <Card className="hover:border-primary/50 transition-colors">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="min-w-0">
                      <div className="font-semibold truncate">{c.name}</div>
                      <div className="text-xs text-muted-foreground mt-1 space-y-0.5">
                        {c.email && <div className="flex items-center gap-1 min-w-0"><Mail className="h-3 w-3 shrink-0" /><span className="truncate">{c.email}</span></div>}
                        {c.phone && <div className="flex items-center gap-1 min-w-0"><Phone className="h-3 w-3 shrink-0" /><span className="truncate">{c.phone}</span></div>}
                        {c.service_address && <div className="flex items-center gap-1 min-w-0"><MapPin className="h-3 w-3 shrink-0" /><span className="truncate">{c.service_address}</span></div>}
                      </div>
                    </div>
                  </div>
                  {c.tags && c.tags.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-1">
                      {c.tags.map(t => <Badge key={t} variant="outline" className="text-[10px]"><Tag className="h-2.5 w-2.5 mr-1" />{t}</Badge>)}
                    </div>
                  )}
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      )}
    </AppShell>
  );
}
